import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API, authFetch } from "@/App";
import {
  TrendingUp, Target, FileText, Calendar, CheckCircle, Clock,
  BarChart3, Loader2, Award, Briefcase, BookOpen
} from "lucide-react";

export function ClientProgressDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchProgress(); }, []);

  const fetchProgress = async () => {
    try {
      const res = await authFetch(`${API}/client/my-progress`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>;
  if (!data) return null;

  const scoreColor = data.score >= 70 ? "text-green-500" : data.score >= 40 ? "text-blue-500" : data.score >= 20 ? "text-amber-500" : "text-red-500";
  const scoreLabel = data.score >= 70 ? "Highly Engaged" : data.score >= 40 ? "Active" : data.score >= 20 ? "Getting Started" : "Jump In!";

  return (
    <div className="space-y-6" data-testid="client-progress-dashboard">
      {/* Score + Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-neutral-200 col-span-2 md:col-span-1">
          <CardContent className="p-4 text-center">
            <div className="relative w-16 h-16 mx-auto mb-2">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.5" fill="none" stroke={data.score >= 70 ? "#22c55e" : data.score >= 40 ? "#3b82f6" : data.score >= 20 ? "#f59e0b" : "#ef4444"}
                  strokeWidth="3" strokeDasharray={`${data.score} ${100 - data.score}`} strokeLinecap="round" />
              </svg>
              <span className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${scoreColor}`}>{data.score}</span>
            </div>
            <p className={`text-xs font-medium ${scoreColor}`}>{scoreLabel}</p>
            <p className="text-[10px] text-neutral-400 mt-1">Member since {data.member_since}</p>
          </CardContent>
        </Card>
        {[
          { icon: Target, label: "Tools Used", value: data.tools_used, color: "text-blue-500" },
          { icon: FileText, label: "Weekly Logs", value: data.weekly_logs, color: "text-emerald-500" },
          { icon: Calendar, label: "Meetings", value: `${data.meetings_completed}/${data.meetings_total}`, color: "text-purple-500" },
          { icon: BookOpen, label: "Engagements", value: data.engagements_enrolled, color: "text-amber-500" },
        ].map(s => (
          <Card key={s.label} className="border-neutral-200">
            <CardContent className="p-4 text-center">
              <s.icon className={`w-5 h-5 ${s.color} mx-auto mb-1`} />
              <p className="text-2xl font-bold text-neutral-900">{s.value}</p>
              <p className="text-[10px] text-neutral-500">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* R.A.P.I.D. Progress */}
      {data.rapid_progress > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-sm text-emerald-900">R.A.P.I.D. Program</span>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">{data.rapid_progress}%</Badge>
            </div>
            <div className="w-full h-2 bg-emerald-200 rounded-full">
              <div className="h-2 bg-emerald-500 rounded-full transition-all" style={{ width: `${data.rapid_progress}%` }} />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Recent Tools */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-blue-500" /> Tools Generated</CardTitle></CardHeader>
          <CardContent>
            {data.tools.length === 0 ? <p className="text-neutral-500 text-xs py-3">No tools generated yet</p> : (
              <div className="space-y-1.5">
                {data.tools.slice(0, 6).map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-neutral-50 rounded text-xs">
                    <span className="text-neutral-700">{(t.tool || "").replace(/_/g, " ")}</span>
                    <span className="text-neutral-400">{t.date}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Meetings */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-purple-500" /> Upcoming Meetings</CardTitle></CardHeader>
          <CardContent>
            {data.upcoming_meetings.length === 0 ? <p className="text-neutral-500 text-xs py-3">No upcoming meetings</p> : (
              <div className="space-y-1.5">
                {data.upcoming_meetings.map((m, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-blue-50 rounded text-xs">
                    <span className="text-neutral-700">{m.title}</span>
                    <span className="text-blue-600">{m.date}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Logs */}
      {data.recent_logs.length > 0 && (
        <Card className="border-neutral-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-500" /> Recent Weekly Logs</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_logs.map((log, i) => (
                <div key={i} className="p-3 bg-neutral-50 rounded-lg text-xs">
                  <div className="flex justify-between mb-1">
                    <span className="text-neutral-400">{log.created_at?.slice(0, 10)}</span>
                  </div>
                  {log.wins && <p className="text-green-700"><strong>Wins:</strong> {log.wins}</p>}
                  {log.challenges && <p className="text-amber-700"><strong>Challenges:</strong> {log.challenges}</p>}
                  {log.focus_next_week && <p className="text-blue-700"><strong>Next Focus:</strong> {log.focus_next_week}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestones */}
      {data.milestones.length > 0 && (
        <Card className="border-neutral-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Briefcase className="w-4 h-4 text-indigo-500" /> Milestones</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {data.milestones.map((m, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-neutral-50 rounded text-xs">
                  {m.status === "completed" ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />}
                  <span className="flex-1 text-neutral-700">{m.title}</span>
                  <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ClientProgressDashboard;
